import { Box, Grid, Typography, useMediaQuery, Paper } from "@mui/material";
import { Suspense } from "react";
import {
  blackColor,
  greyShade74,
  headingColor,
  topbarColor,
  whiteColor,
} from "configs/styles/muiThemes";
import { keyframes } from "@emotion/react";
import ProductHeaderCard from "./MobileUICards/ProductHeaderCard";
import { scrollBox } from "utils/styleUtility";

function InfoCardSection({ commercialDetails }) {
  const matches = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const marqueX = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(80%);
  }
  `;

  return (
    <Suspense fallback={<p></p>}>
      {matches ? (
        <Box pt={1.5} pb={1}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                zIndex: 2,
                left: "1px",
                right: "1px",
                top: "1px",
                bottom: "1px",
                border: "5px dashed #fff",
                // pointerEvents: 'none',
                "-webkit-clip-path": "inset(4px 4px 4px 4px)",
                clipPath: "inset(4px 4px 4px 4px)",
              },
            }}
          >
            <Grid container>
              <Grid item md={2.5} xs={12}>
                <Box
                  sx={{
                    background: "#f2f2f2",
                    height: "50px",
                    display: "flex",
                    // justifyContent: 'center',
                    position: "relative",
                    pl: 1.5,
                    "-webkit-box-align": "center",
                    "-ms-flex-align": "center",
                    alignItems: "center",
                    lineHeight: 1.2,
                    zIndex: 1,
                    whiteSpace: "nowrap",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      right: "-46px",
                      top: "-44px",
                      bottom: "-36px",
                      borderRight: { md: "60px solid #f2f2f2", xs: "none" },
                      "-webkit-transform": "rotate(26deg)",
                      transform: "rotate(26deg)",
                      zIndex: -1,
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: "-62px",
                      top: "-44px",
                      bottom: "-36px",
                      borderRight: { md: "5px solid #f2f2f2", xs: "none" },
                      "-webkit-transform": "rotate(26deg)",
                      transform: "rotate(26deg)",
                      zIndex: -1,
                    },
                  }}
                >
                  <Typography
                    textAlign="start"
                    fontSize={15}
                    fontWeight={600}
                    color={headingColor}
                  >
                    Return Policy & Special Info
                  </Typography>
                </Box>
              </Grid>
              <Grid item md={9.5} xs={12}>
                <Box
                  sx={{
                    background: topbarColor,
                    height: "50px",
                    display: "flex",
                    px: { md: "70px", xs: "0" },
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: { md: "100%", xs: "90vw" },
                      overflow: "hidden",
                      ml: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        display: "inline-block",
                        whiteSpace: "nowrap",
                        animation: `${marqueX} 30s linear infinite`,
                        animationDirection: "reverse",
                      }}
                    >
                      {commercialDetails?.info?.returnPolicy ? (
                        <Typography
                          variant="subtitle1"
                          fontSize={{ xs: 13, md: 14 }}
                          component="p"
                          color={whiteColor}
                          fontWeight={600}
                          display="inline"
                          pr={3}
                        >
                          • {commercialDetails?.info?.returnPolicy}
                        </Typography>
                      ) : null}
                      {commercialDetails?.info?.notes ? (
                        <Typography
                          variant="subtitle1"
                          fontSize={{ xs: 13, md: 14 }}
                          component="p"
                          color={whiteColor}
                          fontWeight={600}
                          display="inline"
                          pr={3}
                        >
                          • {commercialDetails?.info?.notes}
                        </Typography>
                      ) : null}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ) : (
        <Box
          bgcolor={greyShade74}
          p="12px 15px"
          sx={{
            ...scrollBox,
            flexDirection: "row",
          }}
        >
          <ReturnsInfoSection
            title="Return Policy & Special Info"
            infoText={commercialDetails?.info?.returnPolicy}
            textColor={whiteColor}
            background="linear-gradient(to right, #424549 0%, #36393E 100%)"
            imgUrl="/assets/images/new/shipment-fill.svg"
          />

          {commercialDetails?.info?.notes && (
            <ReturnsInfoSection
              title="Customer Notes"
              infoText={commercialDetails?.info?.notes}
              textColor={blackColor}
              background={whiteColor}
              imgUrl="/assets/images/new/notes-outline.svg"
            />
          )}

          {[
            {
              icon: 'https://storage.googleapis.com/bodefaults/shopweb/discountsAndAddress/icon-1.svg',
              title: 'Buy Online Directly From Us,',
              subtitle: 'Lower Prices Every Time!',
            },
            {
              icon: 'https://storage.googleapis.com/bodefaults/shopweb/discountsAndAddress/icon-2.svg',
              title: 'Enjoy Loyalty Points & Coupons',
              subtitle: 'For More Savings!',
            },
            {
              icon: 'https://storage.googleapis.com/bodefaults/shopweb/discountsAndAddress/icon-4.svg',
              title: 'Guaranteed Door-Step Delivery',
              subtitle: 'With Shipment Tracking',
            },
          ].map((item, index) => (
            <ReturnsInfoSection
              key={index}
              title={item.title}
              infoText={item.subtitle}
              textColor={blackColor}
              background={whiteColor}
              imgUrl={item.icon}
            />
          ))}
        </Box>
      )}
    </Suspense>
  );
}

export default InfoCardSection;

const ReturnsInfoSection = ({
  title,
  infoText,
  background,
  textColor,
  imgUrl,
}) => {
  return (
    <Box
      sx={{
        width: "auto",
        flex: "0 0 auto", // Ensures it doesn’t shrink or stretch
        display: "flex",
        gap: 2,
        flexDirection: { xs: "column", sm: "row" },
        mr:4
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: "10px",
          p: "8px 10px",
          background,
          width: "100%", // Fill parent box
        }}
      >
        <Box
          component="img"
          src={imgUrl}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 45,
            height: 45,
            mr: 2,
          }}
        />
        <Box>
          <Typography
            sx={{
              color: textColor,
              fontWeight: 500,
              fontSize: "12px",
              lineHeight: "17px",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              color: textColor,
              opacity: 0.7,
              fontSize: "11px",
              lineHeight: "15px",
            }}
          >
            {infoText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
